import api from '../../api/client';

// --- Zones ---

export interface SpZone {
  id: string;
  organization_id: string | null;
  name: string;
  zone_type: string;
  geometry: Record<string, unknown> | null;
  area_m2: number | null;
  max_floors: number | null;
  ius_min: number | null;
  ius_max: number | null;
  max_height_m: number | null;
  noise_sensitivity: string | null;
  capacity_residents: number | null;
  capacity_jobs: number | null;
  regulations: Record<string, unknown>;
  status: string;
  created_at: string;
  updated_at: string;
}

export async function listZones(skip = 0, limit = 100) {
  const res = await api.get<SpZone[]>('/spatial/zones', { params: { skip, limit } });
  return res.data;
}

export async function getZone(id: string) {
  const res = await api.get<SpZone>(`/spatial/zones/${id}`);
  return res.data;
}

export async function createZone(data: Partial<SpZone>) {
  const res = await api.post<SpZone>('/spatial/zones', data);
  return res.data;
}

export async function updateZone(id: string, data: Partial<SpZone>) {
  const res = await api.put<SpZone>(`/spatial/zones/${id}`, data);
  return res.data;
}

export async function deleteZone(id: string) {
  const res = await api.delete(`/spatial/zones/${id}`);
  return res.data;
}

// --- Permits ---

export interface SpPermitStage {
  id: string;
  permit_id: string;
  from_stage: string;
  to_stage: string;
  action: string;
  comment: string | null;
  performed_by_user_id: string | null;
  created_at: string;
}

export interface SpPermit {
  id: string;
  organization_id: string | null;
  zone_id: string | null;
  reference_number: string;
  applicant_name: string;
  applicant_contact: Record<string, unknown> | null;
  project_title: string;
  project_description: string | null;
  parcel_number: string | null;
  geometry: Record<string, unknown> | null;
  estimated_cost: number | null;
  sbp_total: number | null;
  logements_count: number | null;
  stage: string;
  assigned_to_user_id: string | null;
  documents: unknown[];
  created_by_user_id: string | null;
  submitted_at: string | null;
  decided_at: string | null;
  created_at: string;
  updated_at: string;
  stage_history?: SpPermitStage[];
}

export async function listPermits(params?: { stage?: string; skip?: number; limit?: number }) {
  const res = await api.get<SpPermit[]>('/spatial/permits', { params });
  return res.data;
}

export async function getPermit(id: string) {
  const res = await api.get<SpPermit>(`/spatial/permits/${id}`);
  return res.data;
}

export async function createPermit(data: Partial<SpPermit>) {
  const res = await api.post<SpPermit>('/spatial/permits', data);
  return res.data;
}

export async function updatePermit(id: string, data: Partial<SpPermit>) {
  const res = await api.put<SpPermit>(`/spatial/permits/${id}`, data);
  return res.data;
}

export async function advancePermit(id: string, comment?: string) {
  const res = await api.post<SpPermit>(`/spatial/permits/${id}/advance`, { action: 'advance', comment });
  return res.data;
}

export async function rejectPermit(id: string, comment?: string) {
  const res = await api.post<SpPermit>(`/spatial/permits/${id}/reject`, { action: 'reject', comment });
  return res.data;
}

// --- Capacity ---

export interface CapacityAnalysis {
  total_capacity_residents: number;
  used_capacity_residents: number;
  total_capacity_jobs: number;
  used_capacity_jobs: number;
  zone_breakdown: Record<string, unknown>;
}

export async function getCapacity() {
  const res = await api.get<CapacityAnalysis>('/spatial/capacity');
  return res.data;
}

export async function createCapacitySnapshot() {
  const res = await api.post('/spatial/capacity/snapshot');
  return res.data;
}
